import { useCallback, useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import Button from '../ui/buttons/Button'
import ButtonIcon from '../ui/buttons/ButtonIcon'
import { shareGameLink } from '../../utils/shareGameLink'
import {
    buildGameEndFriendsShareUrl,
    buildGameEndParentShareUrl,
} from '../../utils/gameEndShareUrls'
import { encodeCardIdQueryValue } from '../../utils/cardSharePayload'
import { shareCardImageFromCanvas } from '../../utils/shareCardImage'
import { store } from '../../stores/store'
import { useRunState } from '../../game/provider/useRunState'
import { useRestartRun } from '../../hooks/useRestartRun'
import TextHeadline from '../ui/texts/TextHeadline'
import { useSteleBreakpoint, useSmallMobileBreakpoint } from '@/src/hooks/useBreakpoint'
import clsx from 'clsx'
import IconShare from '../ui/icons/IconShare'
import { playButtonPrimarySound } from '@/src/game/audio/uiSounds'
import IconClose from '../ui/icons/IconClose'
import IconReload from '../ui/icons/IconReload'
import QrCode from '../ui/QrCode'
import { buildCharacterContinueUrl } from '@/src/utils/characterShareUrl'
import IconDownload from '../ui/icons/IconDownload'
import { IconInfo, IconInfoWithOutline } from '../ui/icons/IconInfo'
import { AnimatePresence, motion, Variants } from 'motion/react'
import SaveCardBeforeReplayModal from './SaveCardBeforeReplayModal'
import PostShareOverlay from './PostShareOverlay'
import IconChevron from '../ui/icons/IconChevron'
import IconMagnifyingGlass from '../ui/icons/IconMagnifyingGlass'
import CardInfoOverlay from './CardInfoOverlay'
import TextDefault from '../ui/texts/TextDefault'
import { containerVariants, introModalBackdropVariants, introModalCardVariants } from '@/src/lib/motionVariants'

type GameEndProps = {
    showContent: boolean
    onReplay: () => void
    onOpenEditor: () => void
}

const endCardUiIntroDelayMs = 3600

const rotationHandVariants: Variants = {
    hidden: {
        opacity: 0,
        scale: 0.9,
        y: 12,
    },
    visible: {
        opacity: 1,
        scale: 1,
        y: 0,
        transition: {
            type: 'spring',
            stiffness: 400,
            damping: 12,
            mass: 0.8,
        },
    },
}

/** Win / level complete overlay. */
const GameEnd = observer(function GameEnd({
    showContent,
    onReplay,
    onOpenEditor: _onOpenEditor,
}: GameEndProps) {
    const [showPostShareOverlay, setShowPostShareOverlay] = useState(false)
    const [showCardInfoOverlay, setShowCardInfoOverlay] = useState(false)
    const [cardSaved, setCardSaved] = useState(false)
    const [showSaveBeforeReplayModal, setShowSaveBeforeReplayModal] = useState(false)
    const [steleReplayPromptSeen, setSteleReplayPromptSeen] = useState(false)
    const [showRotationHand, setShowRotationHand] = useState(false)
    const [isInfoOpen, setInfoOpen] = useState<boolean>(false)
    const qrCodeSectionRef = useRef<HTMLDivElement>(null)
    const runState = useRunState()
    const restartRun = useRestartRun()
    const isStele = useSteleBreakpoint()
    const isSmallMobile = useSmallMobileBreakpoint()
    const refDocumentId = store.entryReferrerLandingMatch?.mapping?.documentId
    const resolvedShareUrl = buildCharacterContinueUrl(refDocumentId)

    const parentShareUrl = buildGameEndParentShareUrl(
        refDocumentId,
        encodeCardIdQueryValue(runState, 'parent'),
    )
    const friendsShareUrl = buildGameEndFriendsShareUrl(
        encodeCardIdQueryValue(runState, 'friend'),
    )

    const performShare = useCallback(async () => {
        const ok = await shareGameLink(parentShareUrl, 'parent')
        if (ok) {
            runState.enableEpicCard()
        }
        setShowPostShareOverlay(true)
    }, [parentShareUrl, runState])

    const handleShareFriends = useCallback(async () => {
        setShowPostShareOverlay(false)
        await shareGameLink(friendsShareUrl, 'friend')
    }, [friendsShareUrl])

    const handleClosePostShareOverlay = useCallback(() => {
        setShowPostShareOverlay(false)
    }, [])

    const saveCardImage = useCallback(async () => {
        const ok = await shareCardImageFromCanvas(runState)
        if (!ok) {
            return false
        }
        setCardSaved(true)
        return true
    }, [runState])

    const shouldPromptBeforeReplay = useCallback(() => {
        if (isStele) {
            return !steleReplayPromptSeen
        }
        return !cardSaved
    }, [isStele, steleReplayPromptSeen, cardSaved])

    const performReplay = useCallback(() => {
        if (isStele) {
            onReplay()
            return
        }
        store.setTutorialEnabled(false)
        restartRun('configurator', { withResetTransition: true })
    }, [isStele, onReplay, restartRun])

    const handleReplay = useCallback(() => {
        playButtonPrimarySound()
        if (shouldPromptBeforeReplay()) {
            setShowSaveBeforeReplayModal(true)
            return
        }
        performReplay()
    }, [performReplay, shouldPromptBeforeReplay])

    const handleReplayFromOverlay = useCallback(() => {
        setShowPostShareOverlay(false)
        playButtonPrimarySound()
        performReplay()
    }, [performReplay])

    const handleReplayAnyway = useCallback(() => {
        setShowSaveBeforeReplayModal(false)
        if (isStele) {
            setSteleReplayPromptSeen(false)
        }
        store.setTutorialEnabled(false)
        restartRun('configurator', { withResetTransition: true })
    }, [isStele, restartRun])

    const handleCloseSaveBeforeReplayModal = useCallback(() => {
        setShowSaveBeforeReplayModal(false)
        if (isStele) {
            setSteleReplayPromptSeen(false)
        }
    }, [isStele])

    const handleSaveCardFromModal = useCallback(async () => {
        const ok = await saveCardImage()
        if (ok) {
            setShowSaveBeforeReplayModal(false)
        }
    }, [saveCardImage])

    // const handleShowQrFromModal = useCallback(() => {
    //     setShowSaveBeforeReplayModal(false)
    //     setSteleReplayPromptSeen(true)
    //     qrCodeSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    // }, [])

    const handleEnd = useCallback(() => {
        playButtonPrimarySound()
        store.resetGame()
    }, [])

    const handleGetCard = useCallback(() => {
        runState.setGameEndCardPressed(true)
    }, [runState])

    const handleShowCard = useCallback(() => {
        runState.setGameEndCardPressed(false)
    }, [runState])

    useEffect(() => {
        const showHintTimeout = window.setTimeout(() => {
            setShowRotationHand(true)
        }, 3000)

        const hideRotationHand = () => {
            window.clearTimeout(showHintTimeout)
            setShowRotationHand(false)
        }

        const handleRotation = (e: Event) => {
            const { rotation } = (e as CustomEvent<{ rotation: number }>).detail

            if (Math.abs(rotation) > 0.08) {
                hideRotationHand()
            }
        }

        window.addEventListener('pointerdown', hideRotationHand)
        window.addEventListener('pointermove', hideRotationHand)
        window.addEventListener('touchstart', hideRotationHand)
        window.addEventListener('touchmove', hideRotationHand)
        window.addEventListener('configurator-rotation-change', handleRotation)

        return () => {
            window.clearTimeout(showHintTimeout)
            window.removeEventListener('pointerdown', hideRotationHand)
            window.removeEventListener('pointermove', hideRotationHand)
            window.removeEventListener('touchstart', hideRotationHand)
            window.removeEventListener('touchmove', hideRotationHand)
            window.removeEventListener('configurator-rotation-change', handleRotation)
        }
    }, [])

    if (!showContent) {
        return null
    }

    const topScoringPowerupName = runState.topScoringPowerUp === 'heart' ? 'Sterne' : runState.topScoringPowerUp === 'coin' ? 'Münzen' : runState.topScoringPowerUp === 'shield' ? 'Schutzschilde' : runState.topScoringPowerUp === 'battery' ? 'Batterien' : 'Sterne'
    const introHeadline = `Du hast viele ${topScoringPowerupName} gesammelt!`

    return (
        <div className='z-30 absolute inset-0 flex justify-center items-end px-4 py-2 pointer-events-none'>
            <div
                className='top-8 stele:top-64 absolute animate-game-over-slide-in-top'
                style={{ animationDelay: `${endCardUiIntroDelayMs}ms` }}
            >
                {!runState.getGameEndCardPressed && (
                    <div className='flex flex-col justify-center items-center'>
                        <TextHeadline colorPreset='red' fontSize={isStele ? 128 : 24} maxCharsPerLine={17}>{introHeadline}</TextHeadline>
                    </div>
                )}
            </div>
            {runState.getGameEndCardPressed && (
                <div
                    className={clsx(
                        'absolute flex justify-center text-white animate-game-over-slide-in-bottom pointer-events-auto',
                        'bottom-2/5 gap-2',
                        'stele:top-[1360px] stele:gap-8',
                    )}
                    style={{ animationDelay: `${endCardUiIntroDelayMs}ms` }}
                >
                    <ButtonIcon
                        color='blue'
                        variant='standaloneSmall'
                        icon={<IconMagnifyingGlass width={isStele ? '96' : '24'} height={isStele ? '96' : '24'} />}
                        onClick={handleShowCard}
                        aria-label='Karte anzeigen'
                    />
                    {isStele && (
                        <ButtonIcon
                            color='orange'
                            variant='standaloneSmall'
                            icon={<IconInfoWithOutline width={isStele ? '96' : '24'} height={isStele ? '96' : '24'} />}
                            onClick={() => setInfoOpen(!isInfoOpen)}
                            aria-label='Infos anzeigen'
                        />
                    )}
                </div>
            )}
            <div data-endcard-ui className='flex flex-col items-center gap-2 md:gap-6 p-5 md:p-8 lg:p-10 stele:p-20 rounded-3xl w-full stele:w-250 max-w-2xl min-h-0 max-h-[95vh] overflow-x-hidden overflow-y-auto overscroll-y-contain text-slate-900 touch-pan-y pointer-events-auto'>
                <div
                    className={clsx(
                        'absolute flex flex-col items-center w-full',
                        'bottom-8 max-w-82',
                        'max-xs:bottom-2',
                        'stele:bottom-64 stele:max-w-284',
                        'animate-game-over-slide-in-bottom',
                    )}
                    style={{ animationDelay: `${endCardUiIntroDelayMs}ms` }}
                >

                    <AnimatePresence mode='wait'>
                        {!runState.getGameEndCardPressed ? (
                            <motion.div
                                key='get-card-cta'
                                initial={{ scale: 1.2 }}
                                animate={{ scale: [1.2, 1.5, 1.2] }}
                                exit={{ scale: 1, transition: { duration: 0.15 } }}
                                transition={{
                                    duration: 0.5,
                                    delay: 2.5,
                                    repeatDelay: 2.0,
                                    repeat: Infinity,
                                    repeatType: 'loop',
                                    ease: 'easeInOut',
                                }}
                            // className='w-full'
                            >
                                <Button
                                    onClick={handleGetCard}
                                    type='button'
                                    icon={<IconChevron width={isStele ? '64' : '18'} height={isStele ? '64' : '18'} className='rotate-180' />}
                                    color='blue'
                                    size={isSmallMobile ? 'default' : 'large'}
                                    className={clsx(
                                        '[&_button]:justify-between [&_button]:gap-0 [&_button]:pl-4 [&_button]:pr-2',
                                        'stele:[&_button]:pl-16 stele:[&_button]:pr-8',
                                        '[&_button>svg]:w-auto!',
                                    )}
                                >
                                    Hol dir die Sammelkarte in echt
                                </Button>
                            </motion.div>
                        ) : (
                            !isStele ?
                                <motion.div
                                    key='card-share-panel'
                                    initial={{ opacity: 0, y: 500, scale: 1 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    className={clsx(
                                        'relative flex flex-col justify-center items-center bg-white-gradient drop-shadow-2-white-stroke border border-white-stroke w-full max-w-min max-h-min pattern-background',
                                        'px-4 py-6 rounded-3xl min-w-82 gap-4',
                                        'max-xs:px-3 max-xs:py-3 max-xs:gap-2 max-xs:rounded-2xl max-xs:min-w-0 max-xs:w-full',
                                        'stele:px-16 stele:py-24 stele:rounded-[96px] min-w-stele:w-328 stele:gap-[64px]'
                                    )}
                                >
                                    <TextHeadline
                                        className='-top-13 left-1/2 absolute -translate-x-1/2 max-xs:-top-11'
                                        colorPreset='red'
                                        fontSize={isSmallMobile ? 20 : 24}
                                        maxCharsPerLine={20}
                                        leading={isSmallMobile ? 1.1 : 1.25}
                                        tracking='-2%'
                                    >
                                        Diese Sammelkarte Gibts auch in echt!
                                    </TextHeadline>
                                    <button
                                        type='button'
                                        className={clsx(
                                            'hover:opacity-80 text-white-stroke-even-darker font-nunito font-bold text-center tracking-[-4%] transition-opacity cursor-pointer',
                                            'text-[16px] leading-5.5',
                                            'max-xs:text-[12px] max-xs:leading-4',
                                        )}
                                        onClick={() => setShowCardInfoOverlay(true)}
                                    >
                                        Schick sie deinen Eltern, damit ihr sie in eurer Sparkasse abholen könnt.{' '}
                                        <IconInfo
                                            width={isSmallMobile ? '12' : '16'}
                                            height={isSmallMobile ? '12' : '16'}
                                            className='inline-block mb-1'
                                        />
                                    </button>
                                    <Button
                                        onClick={performShare}
                                        type='button'
                                        icon={<IconShare />}
                                        color='orange'
                                        size='large'
                                        multiline={true}
                                    >
                                        ZEIG&apos;S DEINEN ELTERN
                                    </Button>
                                    <div className='flex gap-1 md:gap-2 lg:gap-5 w-full min-w-0'>
                                        <Button type='button' icon={<IconDownload fill='#5486AC' />} color='white' onClick={saveCardImage} className='min-w-34'>
                                            Karte speichern
                                        </Button>
                                        <Button type='button' icon={<IconReload />} color='white' onClick={handleReplay} className='min-w-34'>
                                            Nochmal spielen
                                        </Button>
                                    </div>
                                </motion.div>
                                :
                                <>
                                    <div
                                        ref={qrCodeSectionRef}
                                        className={clsx(
                                            'relative flex flex-col items-center drop-shadow-2-white-stroke-darker bg-white-stroke-lighter border-white-stroke pattern-background',
                                            'gap-16 p-16 border-4 rounded-[96px] max-w-full min-w-308 pt-24'
                                        )}
                                    >
                                        <TextHeadline
                                            className='-top-46 left-1/2 absolute -translate-x-1/2'
                                            colorPreset='red'
                                            fontSize={96}
                                            maxCharsPerLine={20}
                                            leading={0.95}
                                            tracking='-2%'
                                        >
                                            Diese Sammelkarte Gibts auch in echt!
                                        </TextHeadline>

                                        <p className='text-white-stroke-even-darker max-w-[1004px] font-nunito font-bold text-[64px] text-center leading-[88px] tracking-[-2%]'>
                                            Hol dir deine Karte in echt beim Berater oder digital aufs Handy{' '}
                                        </p>

                                        <div className='flex items-center gap-6 bg-white-stroke p-6 rounded-[48px] w-full'>
                                            <div className='bg-white p-4 rounded-3xl shrink-0'>
                                                <QrCode
                                                    value={resolvedShareUrl}
                                                    size={224}
                                                    color='#0E0622'
                                                    background='#FFFFFF'
                                                />
                                            </div>
                                            {/* <p className='text-white-stroke-lightest font-nunito font-normal text-[48px] leading-[135%] tracking-[-2%]' style={{ textShadow: '0 4px 0 #06487C' }}></p> */}
                                            <div className='z-50 flex flex-col justify-center'>
                                                <TextDefault fontSize={48} leading={0.8} textColor='#FFF' outlineColor='#06487C' dropShadowColor='#06487C' >
                                                    Scanne den Code und
                                                </TextDefault>
                                                <TextDefault fontSize={48} textColor='#FFF' outlineColor='#06487C' dropShadowColor='#06487C'>
                                                    spiel zuhause weiter.
                                                </TextDefault>
                                            </div>
                                        </div>

                                        <div className='flex justify-between gap-4 w-full'>
                                            <Button type='button' icon={<IconReload width='100%' height='100%' />} color='white' onClick={handleReplay}>
                                                Nochmal spielen
                                            </Button>
                                            <Button type='button' icon={<IconClose width='100%' height='100%' />} color='red' onClick={handleEnd}>
                                                Beenden
                                            </Button>
                                        </div>
                                    </div>
                                </>

                        )}
                    </AnimatePresence>
                </div>
            </div>
            <AnimatePresence>
                {showRotationHand ? (
                    <motion.div
                        key='rotation-hand'
                        variants={rotationHandVariants}
                        initial='hidden'
                        animate='visible'
                        exit={{
                            opacity: 0,
                            scale: 0.92,
                            transition: {
                                duration: 0.18,
                                ease: 'easeOut',
                            },
                        }}
                        className='z-10 absolute inset-0 flex justify-center items-center mb-60 stele:mb-240 pointer-events-none'
                    >
                        <motion.div
                            animate={{
                                x: isStele ? [-600, 600, -600] : [-150, 150, -150],
                                y: isStele ? [1160, 1340, 1160, 1340, 1160] : [260, 340, 260, 340, 260],
                                scale: [0.75, 1.0, 0.75, 1.0, 0.75],
                                opacity: [0.0, 1.0, 1.0, 1.0, 0.0],
                            }}
                            transition={{
                                duration: 3.0,
                                delay: 2.5,
                                repeatDelay: 2.0,
                                repeat: Infinity,
                                repeatType: 'loop',
                                ease: 'easeInOut',
                            }}
                        >
                            <img
                                src='/images/hands/State=Point.png'
                                alt='hand'
                                className={clsx(
                                    'w-16 h-16',
                                    'stele:w-60 stele:h-60'
                                )}
                            />
                        </motion.div>
                    </motion.div>
                ) : null}
            </AnimatePresence>
            <SaveCardBeforeReplayModal
                open={showSaveBeforeReplayModal}
                variant={isStele ? 'stele' : 'default'}
                onClose={handleCloseSaveBeforeReplayModal}
                onPrimaryAction={isStele ? handleCloseSaveBeforeReplayModal : handleSaveCardFromModal}
                onReplayAnyway={handleReplayAnyway}
            />
            {!isStele && (
                <PostShareOverlay
                    open={showPostShareOverlay}
                    onClose={handleClosePostShareOverlay}
                    onReplay={handleReplayFromOverlay}
                    onShareFriends={handleShareFriends}
                />
            )}
            {!isStele && (
                <CardInfoOverlay
                    open={showCardInfoOverlay}
                    onClose={() => setShowCardInfoOverlay(false)}
                />
            )}

            <motion.div
                variants={containerVariants}
                initial='hidden'
                animate='visible'
                className='absolute inset-0 flex flex-col items-center'
            >
                <AnimatePresence>
                    {isInfoOpen &&
                        <motion.div
                            className='z-500 absolute inset-0 flex justify-center items-center stele:items-center bg-black/65 backdrop-blur-sm pointer-events-auto'
                            variants={introModalBackdropVariants}
                            initial='hidden'
                            animate='visible'
                            exit='exit'
                        >
                            <motion.div
                                className={clsx(
                                    'relative flex flex-col justify-center items-center'
                                )}
                                variants={introModalCardVariants}
                            >
                                <img src='/images/Content.png' className='stele:w-auto stele:h-429 object-cover' />
                                <div className='bottom-6 stele:-bottom-24 absolute'>
                                    <ButtonIcon icon={<IconClose width='100%' height='100%' />} variant='standalone' color='red' popupCloseSound activateOn='pointerup' onClick={() => setInfoOpen(!isInfoOpen)} />
                                </div>
                            </motion.div>
                        </motion.div>
                    }
                </AnimatePresence>
            </motion.div>
        </div>
    )
})

export default GameEnd
